import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Flame, Trees, CircleAlert } from "lucide-react";
import PageHeader from "../../page_header";
import AlertLocationMap from "../AlertLocationMap";
import { guardianAlertService } from "../../../services/guardianAlertService";
import {
  classifyAlertType,
  isAlreadyAwareAction,
  normalizeTicketAction,
  pickAlertId,
  resolveAlertResponseState,
  severityTone,
} from "../../../utils/alertResponseState";
import {
  isValidLatLon,
  parseCoordinatePair,
} from "../../../utils/alertRequestCoordinates";
import {
  PageShell,
  Intro,
  DetailCard,
  MetaRow,
  Badge,
  CheckboxRow,
  ActionsRow,
  PrimaryButton,
  GhostButton,
  LoadingText,
  ErrorText,
  EmptyState,
  DescriptionBox,
  InfoList,
  InfoRow,
  InfoLabel,
  InfoValue,
  LocationBlock,
  CoordinatesText,
  MapsLink,
  SectionTitle,
  TypeIconWrap,
} from "../styles";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function statusLabelFromAction(action) {
  const a = normalizeTicketAction(action);
  const map = {
    aguardando_resposta: "Aguardando resposta",
    equipe_enviada: "Equipe enviada",
    respondido_processo_sucessao: "Respondido",
    resposta_rejeitado: "Resposta rejeitada",
    criado: "Criado",
  };
  return map[a] || action || "Aguardando ciência";
}

function TypeIcon({ kind }) {
  if (kind === "incendio") return <Flame size={18} strokeWidth={2.2} />;
  if (kind === "desmatamento") return <Trees size={18} strokeWidth={2.2} />;
  return <CircleAlert size={18} strokeWidth={2.2} />;
}

/**
 * Detalhe do alerta (espelho do AlertaRecebido do Guardian app)
 * + mapa embutido Leaflet no ponto das coordenadas.
 */
export default function AlertReceived() {
  const { alertId: alertIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const preview = location.state?.alertPreview || null;

  const [alert, setAlert] = useState(preview);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ciente, setCiente] = useState(false);
  const [checkboxDisabled, setCheckboxDisabled] = useState(false);
  const [responseState, setResponseState] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [lastTicketAction, setLastTicketAction] = useState("");

  const alertId = String(alertIdParam || pickAlertId(preview) || "");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!alertId) {
        setError("Alerta não encontrado.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const [tickets, list] = await Promise.all([
          guardianAlertService.getTicketsForAlert(alertId),
          guardianAlertService.getAlertsByUser().catch(() => []),
        ]);
        if (cancelled) return;

        const state = resolveAlertResponseState(tickets);
        setResponseState(state);

        const found =
          (Array.isArray(list) &&
            list.find(
              (a) =>
                String(pickAlertId(a)) === String(alertId) ||
                String(a.id) === String(alertId)
            )) ||
          preview;

        if (found) setAlert(found);

        const last = found?.last_ticket;
        if (last) {
          setLastTicketAction(
            last.action_display || statusLabelFromAction(last.action)
          );
          if (isAlreadyAwareAction(last.action)) {
            setCiente(true);
            setCheckboxDisabled(true);
          }
        } else if (tickets.length) {
          const sorted = [...tickets].sort(
            (a, b) =>
              new Date(b.ticket_created_at || b.created_at || 0) -
              new Date(a.ticket_created_at || a.created_at || 0)
          );
          const t = sorted[0];
          setLastTicketAction(
            t.action_display || statusLabelFromAction(t.action)
          );
          if (tickets.some((x) => isAlreadyAwareAction(x.action))) {
            setCiente(true);
            setCheckboxDisabled(true);
          }
        } else {
          setLastTicketAction("Aguardando ciência");
        }

        if (state === "rejected_needs_new") {
          const rejected = [...(tickets || [])].find(
            (t) =>
              String(t.action || "")
                .toLowerCase()
                .includes("rejeit") && t.rejection_reason
          );
          setRejectionReason(rejected?.rejection_reason || "");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Não foi possível carregar o alerta.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [alertId, preview]);

  const coordsParsed = useMemo(() => {
    const raw = alert?.coordinates;
    const parsed = parseCoordinatePair(raw);
    if (!parsed || !isValidLatLon(parsed.lat, parsed.lon)) return null;
    return parsed;
  }, [alert?.coordinates]);

  const confirmScience = async () => {
    if (!ciente || checkboxDisabled || submitting) return false;
    setSubmitting(true);
    setError("");
    try {
      await guardianAlertService.createFieldAlertTicket({
        alertId,
        action: "aguardando_resposta",
        responses: "nenhuma",
        observations: "nenhuma",
      });
      setCheckboxDisabled(true);
      setResponseState("needs_equipe");
      setLastTicketAction("Aguardando resposta");
      return true;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setCheckboxDisabled(true);
        setCiente(true);
        return true;
      }
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Não foi possível registrar a ciência."
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const goRespond = async () => {
    if (!ciente) {
      setError("Confirme que está ciente do alerta antes de responder.");
      return;
    }
    if (!checkboxDisabled) {
      const ok = await confirmScience();
      if (!ok) return;
    }
    navigate(`/my-alerts/${alertId}/respond`, {
      state: {
        alertPreview: alert,
        responseState,
      },
    });
  };

  const handleResponderDepois = async () => {
    if (!ciente) {
      setError("Marque “Estou ciente” antes de continuar.");
      return;
    }
    if (!checkboxDisabled) {
      const ok = await confirmScience();
      if (!ok) return;
    }
    navigate("/my-alerts");
  };

  if (loading) {
    return (
      <PageShell>
        <PageHeader
          title="Alerta"
          actionLabel="Voltar"
          onAction={() => navigate("/my-alerts")}
        />
        <LoadingText>Carregando detalhes do alerta…</LoadingText>
      </PageShell>
    );
  }

  if (!alert && error) {
    return (
      <PageShell>
        <PageHeader
          title="Alerta"
          actionLabel="Voltar"
          onAction={() => navigate("/my-alerts")}
        />
        <EmptyState>{error}</EmptyState>
      </PageShell>
    );
  }

  const severity = alert?.severity_level_description || "";
  const typeDesc = alert?.alert_type_description || "";
  const kind = classifyAlertType(typeDesc);
  const tone = severityTone(severity);
  const isRespondido = responseState === "already_answered";
  const showResponderActions =
    responseState === "needs_equipe" || responseState === "needs_resposta";
  const googleMapsUrl = coordsParsed
    ? `https://www.google.com/maps?q=${coordsParsed.lat},${coordsParsed.lon}`
    : alert?.coordinates
      ? `https://www.google.com/maps?q=${encodeURIComponent(alert.coordinates)}`
      : null;

  return (
    <PageShell>
      <PageHeader
        title={isRespondido ? "Alerta respondido" : "Você possui um alerta ativo"}
        actionLabel="Voltar"
        onAction={() => navigate("/my-alerts")}
      />
      <Intro>
        Confira os dados da ocorrência, a localização no mapa e registre a
        ciência e a resposta — mesmo fluxo do aplicativo Guardian.
      </Intro>

      <DetailCard>
        <MetaRow>
          <TypeIconWrap $tone={tone}>
            <TypeIcon kind={kind} />
          </TypeIconWrap>
          <Badge $tone={tone}>{severity || "Severidade —"}</Badge>
          <Badge $tone="low">{typeDesc || "Tipo —"}</Badge>
          {alert?.cod_alerta ? (
            <Badge $tone="low">Código {alert.cod_alerta}</Badge>
          ) : null}
        </MetaRow>

        <DescriptionBox>
          {alert?.observation || "Descrição não disponível."}
        </DescriptionBox>

        <InfoList>
          <InfoRow>
            <InfoLabel>Código</InfoLabel>
            <InfoValue>{alert?.cod_alerta || "Não disponível"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Tipo</InfoLabel>
            <InfoValue>{typeDesc || "—"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Severidade</InfoLabel>
            <InfoValue>
              <Badge $tone={tone}>{severity || "Não informado"}</Badge>
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Data</InfoLabel>
            <InfoValue>{formatDate(alert?.alert_created_at)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Status</InfoLabel>
            <InfoValue>{lastTicketAction || "—"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Área</InfoLabel>
            <InfoValue>{alert?.area_title || "—"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Projeto</InfoLabel>
            <InfoValue>{alert?.project_title || "—"}</InfoValue>
          </InfoRow>
        </InfoList>

        {alert?.coordinates ? (
          <LocationBlock>
            <SectionTitle>Localização</SectionTitle>
            <CoordinatesText>
              Coordenadas: <strong>{alert.coordinates}</strong>
            </CoordinatesText>
            {coordsParsed ? (
              <AlertLocationMap
                lat={coordsParsed.lat}
                lon={coordsParsed.lon}
                label={
                  alert?.cod_alerta
                    ? `Alerta ${alert.cod_alerta}`
                    : typeDesc || "Alerta"
                }
              />
            ) : (
              <ErrorText>
                Não foi possível interpretar as coordenadas para o mapa.
              </ErrorText>
            )}
            {googleMapsUrl ? (
              <MapsLink
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                📍 Abrir no Google Maps
              </MapsLink>
            ) : null}
          </LocationBlock>
        ) : (
          <CoordinatesText>Coordenadas não disponíveis para este alerta.</CoordinatesText>
        )}

        {rejectionReason ? (
          <ErrorText>Motivo da rejeição anterior: {rejectionReason}</ErrorText>
        ) : null}

        {isRespondido ? (
          <>
            <EmptyState>
              <strong>Resposta já registrada</strong>
              <p>Você pode consultar o histórico deste alerta.</p>
            </EmptyState>
            <ActionsRow>
              <PrimaryButton
                type="button"
                onClick={() =>
                  navigate(`/my-alerts/${alertId}/respond`, {
                    state: { alertPreview: alert, viewOnly: true },
                  })
                }
              >
                Ver resposta registrada
              </PrimaryButton>
              <GhostButton type="button" onClick={() => navigate("/my-alerts")}>
                Voltar à lista
              </GhostButton>
            </ActionsRow>
          </>
        ) : (
          <>
            <CheckboxRow data-disabled={checkboxDisabled ? "true" : "false"}>
              <input
                type="checkbox"
                checked={ciente}
                disabled={checkboxDisabled || submitting}
                onChange={(e) => setCiente(e.target.checked)}
              />
              <span>
                Estou ciente que este alerta está ativo na minha área
              </span>
            </CheckboxRow>

            {error ? <ErrorText>{error}</ErrorText> : null}

            {responseState === "rejected_needs_new" ? (
              <>
                <SectionTitle>Resposta rejeitada</SectionTitle>
                <ActionsRow>
                  <PrimaryButton
                    type="button"
                    disabled={!ciente || submitting}
                    onClick={goRespond}
                  >
                    Registrar nova resposta
                  </PrimaryButton>
                </ActionsRow>
              </>
            ) : null}

            {showResponderActions ? (
              <>
                <SectionTitle>Deseja responder agora?</SectionTitle>
                <ActionsRow>
                  <GhostButton
                    type="button"
                    disabled={!ciente || submitting}
                    onClick={handleResponderDepois}
                  >
                    Responder depois
                  </GhostButton>
                  <PrimaryButton
                    type="button"
                    disabled={!ciente || submitting}
                    onClick={goRespond}
                  >
                    {submitting ? "Processando…" : "Responder agora"}
                  </PrimaryButton>
                </ActionsRow>
              </>
            ) : null}

            {!showResponderActions &&
            responseState !== "rejected_needs_new" &&
            !checkboxDisabled ? (
              <ActionsRow>
                <GhostButton
                  type="button"
                  disabled={!ciente || submitting}
                  onClick={confirmScience}
                >
                  {submitting ? "Registrando…" : "Confirmar ciência"}
                </GhostButton>
              </ActionsRow>
            ) : null}
          </>
        )}
      </DetailCard>
    </PageShell>
  );
}
